import { useLocalSearchParams, useRouter } from 'expo-router';
import type { ReactNode } from 'react';
import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation } from 'urql';
import { isRichTextEmpty } from '@/components/RichText/RichText.utils';
import RichTextDisplay from '@/components/RichText/RichTextDisplay';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import Text from '@/components/UI/Text';
import { type FragmentOf, graphql, readFragment } from '@/gql/graphql';
import useHaptics from '@/hooks/useHaptics';
import Avatar from '../Custom/Avatar';
import {
  EventRsvpEditorAccessFragment,
  getEventRsvpEditorAccess,
} from './EventRsvpEditorAccess';
import * as EventRsvpPicker from './EventRsvpPicker';

type RsvpStatus = ReturnType<typeof graphql.scalar<'rsvp_status_enum'>>;
type RsvpRowKind = 'managed' | 'self' | 'member';
type RsvpOptionName = 'Maybe' | 'No' | 'None' | 'Yes';

type RsvpOption = {
  label: string;
  name: RsvpOptionName;
  status: RsvpStatus | null;
};

const rsvpOptions: readonly RsvpOption[] = [
  { label: 'No Response', name: 'None', status: null },
  { label: 'No', name: 'No', status: 'no' },
  { label: 'Maybe', name: 'Maybe', status: 'maybe' },
  { label: 'Yes', name: 'Yes', status: 'yes' },
];

export const EventRsvpListFragment = graphql(/* GraphQL */ `
  fragment EventRsvpList on query_root {
    person_by_pk(id: $personId) {
      id
      name
      avatar_id
      color
      rsvp(args: { event_id: $eventId })
      event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
        event_id
        person_id
        notes
        status
      }
    }
    selectedRsvpPerson: person_by_pk(id: $subjectPersonId) {
      id
      name
      avatar_id
      color
      rsvp(args: { event_id: $eventId })
      event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
        event_id
        person_id
        notes
        status
      }
    }
    person_manager(
      order_by: { person: { name: asc } }
      where: {
        manager_id: { _eq: $personId }
        person_event_participants: { event_id: { _eq: $eventId } }
      }
    ) {
      manager_id
      person_id
      person {
        id
        name
        avatar_id
        color
        rsvp(args: { event_id: $eventId })
        event_rsvp: rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
          event_id
          person_id
          notes
          status
        }
      }
      team {
        id
        title
        home_primary_color
      }
    }
    ...EventRsvpEditorAccess
  }
`, [EventRsvpEditorAccessFragment]);

function normalizeRsvpStatus(
  status: string | null | undefined,
): RsvpStatus | null {
  if (status === 'maybe' || status === 'no' || status === 'yes') {
    return status;
  }

  return null;
}

function getRowTestId(rowKind: RsvpRowKind, personId: string) {
  if (rowKind === 'managed') {
    return `EventRsvpManagedRow-${personId}`;
  }
  if (rowKind === 'member') {
    return `EventRsvpMemberRow-${personId}`;
  }
  return `EventRsvpRow-${personId}`;
}

function getButtonTestId(
  rowKind: RsvpRowKind,
  personId: string,
  optionName: RsvpOptionName,
) {
  if (rowKind === 'managed') {
    return `EventRsvpManagedButton${optionName}-${personId}`;
  }
  if (rowKind === 'member') {
    return `EventRsvpMemberButton${optionName}-${personId}`;
  }
  return `EventRsvpButton${optionName}`;
}

function RsvpResponseControl({
  isSaving,
  name,
  onChange,
  personId,
  rowKind,
  status,
}: {
  isSaving: boolean;
  name: string;
  onChange: (status: RsvpStatus | null) => void;
  personId: string;
  rowKind: RsvpRowKind;
  status: RsvpStatus | null;
}) {
  return (
    <View
      accessibilityLabel={`RSVP response for ${name}`}
      accessibilityRole="radiogroup"
      style={styles.responseControl}
    >
      {rsvpOptions.map((option) => {
        const isSelected = status === option.status;
        const isDisabled = isSaving || isSelected;

        return (
          <EventRsvpPicker.Button
            accessibilityLabel={`${option.label} for ${name}`}
            accessibilityState={{ disabled: isDisabled, selected: isSelected }}
            aria-pressed={isSelected}
            disabled={isDisabled}
            forceOpacity
            isSelected={isSelected}
            key={option.name}
            onPress={() => onChange(option.status)}
            outline={!isSelected}
            rsvp={option.status}
            size="md"
            testID={getButtonTestId(rowKind, personId, option.name)}
          />
        );
      })}
    </View>
  );
}

function RsvpPersonCard({
  avatarId,
  color,
  isSaving,
  name,
  notes,
  onPressNotes,
  onPressRsvp,
  personId,
  rowKind,
  status,
  teamTitle,
}: {
  avatarId: string;
  color: string;
  isSaving: boolean;
  name: string;
  notes: string;
  onPressNotes: () => void;
  onPressRsvp: (status: RsvpStatus | null) => void;
  personId: string;
  rowKind: RsvpRowKind;
  status: RsvpStatus | null;
  teamTitle?: string;
}) {
  const hasNotes = !isRichTextEmpty(notes);

  return (
    <Card testID={getRowTestId(rowKind, personId)}>
      <Box p="md" space="md">
        <Box alignItems="center" direction="horizontal" space="sm">
          <Avatar color={color} id={avatarId} name={name} size="md" />
          <Box flex minWidth={0} space="xs">
            <Text
              flexShrink={1}
              fontWeight="semibold"
              numberOfLines={2}
              size="lg"
            >
              {name}
            </Text>
            {teamTitle ? (
              <Text
                flexShrink={1}
                numberOfLines={1}
                size="xs"
                variant="typographySecondary"
              >
                {teamTitle}
              </Text>
            ) : null}
          </Box>
          <RsvpResponseControl
            isSaving={isSaving}
            name={name}
            onChange={onPressRsvp}
            personId={personId}
            rowKind={rowKind}
            status={status}
          />
        </Box>
        <View style={styles.divider} />
        <Box alignItems="flex-start" direction="horizontal" space="md">
          <Box flex minWidth={0}>
            {hasNotes ? (
              <RichTextDisplay html={notes} size="sm" />
            ) : (
              <Text size="xs" variant="typographySecondary">
                No RSVP note
              </Text>
            )}
          </Box>
          <ActionButton
            accessibilityLabel={`${hasNotes ? 'Edit' : 'Add'} RSVP note for ${name}`}
            forceLabel
            icon={hasNotes ? 'create-outline' : 'add-outline'}
            label={hasNotes ? 'Edit Note' : 'Add Note'}
            onPress={onPressNotes}
            size="xs"
            testID={`EventRsvpNotesButton-${personId}`}
            variant="secondary"
          />
        </Box>
      </Box>
    </Card>
  );
}

export default function EventRsvpList({
  fragment,
  personId,
  subjectPersonId,
}: {
  fragment: FragmentOf<typeof EventRsvpListFragment> | null | undefined;
  personId: string;
  subjectPersonId?: string;
}) {
  const { eventId } = useLocalSearchParams<{ eventId: string }>();
  const router = useRouter();
  const [upsertRsvpResult, upsertRsvp] = useMutation(
    EventRsvpPicker.UpsertRsvp,
  );
  const data = readFragment(EventRsvpListFragment, fragment);
  const you = data?.person_by_pk;
  const managedPeople = data?.person_manager ?? [];
  const selectedPerson = data?.selectedRsvpPerson;
  const access = getEventRsvpEditorAccess({
    fragment: data,
    personId,
    subjectPersonId: subjectPersonId ?? personId,
  });
  const onPressRsvp = useHaptics(
    (targetPersonId: string, status: RsvpStatus | null) => {
      void upsertRsvp({ eventId, status, personId: targetPersonId });
    },
  );
  const onPressNotes = (targetPersonId: string) => {
    router.push({
      pathname: '/event/[eventId]/rsvp/notes',
      params: { eventId, personId: targetPersonId },
    });
  };
  const renderPersonCard = ({
    avatarId,
    color,
    name,
    notes,
    personCardId,
    rowKind,
    status,
    teamTitle,
  }: {
    avatarId: string;
    color: string;
    name: string;
    notes: string;
    personCardId: string;
    rowKind: RsvpRowKind;
    status: RsvpStatus | null;
    teamTitle?: string;
  }) => (
    <RsvpPersonCard
      avatarId={avatarId}
      color={color}
      isSaving={upsertRsvpResult.fetching}
      name={name}
      notes={notes}
      onPressNotes={() => onPressNotes(personCardId)}
      onPressRsvp={(nextStatus) => onPressRsvp(personCardId, nextStatus)}
      personId={personCardId}
      rowKind={rowKind}
      status={status}
      teamTitle={teamTitle}
    />
  );

  let content: ReactNode = null;

  if (subjectPersonId === personId && you && access.canEdit) {
    content = renderPersonCard({
      avatarId: you.avatar_id ?? '',
      color: you.color ?? '',
      name: you.name ?? '',
      notes: you.event_rsvp[0]?.notes ?? '',
      personCardId: you.id,
      rowKind: 'self',
      status: normalizeRsvpStatus(you.rsvp),
    });
  } else if (subjectPersonId && access.canEdit && selectedPerson) {
    const selectedManagedPerson = managedPeople.find(
      (manager) => manager.person_id === selectedPerson.id,
    );
    content = renderPersonCard({
      avatarId: selectedPerson.avatar_id ?? '',
      color:
        access.team?.home_primary_color ??
        selectedManagedPerson?.team?.home_primary_color ??
        selectedPerson.color ??
        '',
      name: selectedPerson.name ?? '',
      notes: selectedPerson.event_rsvp[0]?.notes ?? '',
      personCardId: selectedPerson.id,
      rowKind:
        access.source === 'team' || access.source === 'event'
          ? 'member'
          : 'managed',
      status: normalizeRsvpStatus(selectedPerson.rsvp),
      teamTitle: access.team?.title ?? selectedManagedPerson?.team?.title,
    });
  } else if (subjectPersonId && !access.canEdit) {
    content = <Text>You do not have permission to edit this RSVP.</Text>;
  } else {
    content = (
      <>
        {you && access.canEdit
          ? renderPersonCard({
              avatarId: you.avatar_id ?? '',
              color: you.color ?? '',
              name: you.name ?? '',
              notes: you.event_rsvp[0]?.notes ?? '',
              personCardId: you.id,
              rowKind: 'self',
              status: normalizeRsvpStatus(you.rsvp),
            })
          : null}
        {managedPeople.map((managedPerson) => {
          const managedPersonData = managedPerson.person;
          return (
            <View key={managedPerson.person_id}>
              {renderPersonCard({
                avatarId: managedPersonData.avatar_id ?? '',
                color:
                  managedPerson.team?.home_primary_color ??
                  managedPersonData.color ??
                  '',
                name: managedPersonData.name ?? '',
                notes: managedPersonData.event_rsvp[0]?.notes ?? '',
                personCardId: managedPersonData.id,
                rowKind: 'managed',
                status: normalizeRsvpStatus(managedPersonData.rsvp),
                teamTitle: managedPerson.team?.title ?? undefined,
              })}
            </View>
          );
        })}
      </>
    );
  }

  return <Box space="md">{content}</Box>;
}

const styles = StyleSheet.create((theme) => ({
  responseControl: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.size(1),
    justifyContent: 'flex-end',
  },
  divider: {
    backgroundColor: theme.colors.borderMuted,
    height: StyleSheet.hairlineWidth,
  },
}));
